import React from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../components/ScrollReveal';

const Privacy = () => (
  <main className="bg-void min-h-screen pt-28 pb-20 px-4">
    <div className="max-w-4xl mx-auto">
      <ScrollReveal>
        <h1 className="text-3xl sm:text-5xl font-bold font-mono mb-4">
          Privacy <span className="text-lime">Policy</span>
        </h1>
        <p className="text-muted mb-8">Last updated: April 12, 2026</p>
      </ScrollReveal>

      <ScrollReveal delay={100}>
        <article className="glass-card rounded-xl p-8">
          <div className="text-muted leading-relaxed space-y-6">
            <p>
              This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
            </p>
            <p>
              We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold font-mono text-lime mt-8 mb-4">
              Interpretation and Definitions
            </h2>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Interpretation</h3>
            <p>
              The words whose initial letters are capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Definitions</h3>
            <p>For the purposes of this Privacy Policy:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong className="text-white">Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
              <li><strong className="text-white">Affiliate</strong> means an entity that controls, is controlled by, or is under common control with a party, where &quot;control&quot; means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.</li>
              <li><strong className="text-white">Application</strong> refers to OLED Burn Care, the software program provided by the Company.</li>
              <li><strong className="text-white">Company</strong> (referred to as either &quot;the Company&quot;, &quot;We&quot;, &quot;Us&quot; or &quot;Our&quot; in this Agreement) refers to OLED Burn Care.</li>
              <li><strong className="text-white">Cookies</strong> are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.</li>
              <li><strong className="text-white">Country</strong> refers to: Palestinian Territories</li>
              <li><strong className="text-white">Device</strong> means any device that can access the Service such as a computer, a cell phone or a digital tablet.</li>
              <li><strong className="text-white">Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
              <li><strong className="text-white">Service</strong> refers to the Application or the Website or both.</li>
              <li><strong className="text-white">Service Provider</strong> means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.</li>
              <li><strong className="text-white">Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).</li>
              <li><strong className="text-white">Website</strong> refers to OLED Burn Care, accessible from http://www.oledburncare.com</li>
              <li><strong className="text-white">You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
            </ul>

            <h2 className="text-2xl font-bold font-mono text-lime mt-8 mb-4">
              Collecting and Using Your Personal Data
            </h2>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Types of Data Collected</h3>

            <div className="bg-lime/10 border border-lime rounded-lg p-6">
              <h4 className="text-lg font-semibold text-lime mb-3">App Data and Ads</h4>
              <p className="text-white">
                <strong>Overlay configurations are stored locally on your device.</strong> The app also uses ads to support the service. Advertising and mediation providers may process device and ad-related information (for example advertising identifiers, IP address, app/device details, and ad interaction data) to deliver and measure ads.
              </p>
            </div>

            <p>
              Core burn-in protection features run locally on your device. Network access is used for ad delivery and related privacy choices where applicable.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Tracking Technologies and Cookies</h3>
            <p>
              <strong className="text-lime">The app uses advertising technologies from verified ad partners.</strong> These technologies can be used to serve ads, limit ad fraud, and measure ad performance, subject to applicable laws and your consent choices.
            </p>
            <p>
              This website may use basic cookies only for essential functionality (such as remembering if you&apos;ve accepted the cookie notice). No tracking or analytics cookies are used.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Use of Your Personal Data</h3>
            <p className="text-lime font-semibold mb-4">
              We use data related to ad delivery and app operation to provide and improve the Service.
            </p>
            <p>
              This includes local app settings and ad-related processing by verified ad partners. For users in applicable regions, consent choices are handled through supported consent tools in the app.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Retention of Your Personal Data</h3>
            <p>
              Local settings remain on your device. Ad-related data retention is managed by verified ad partners according to their policies and applicable law.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Transfer of Your Personal Data</h3>
            <p>
              Ad-related data may be processed and transferred by verified ad partners as required to deliver, secure, and measure ads.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Delete Your Personal Data</h3>
            <p>
              You can delete local app data by clearing app storage or uninstalling the app. For ad-related data controls, use the in-app privacy options where available.
            </p>

            <h2 className="text-2xl font-bold font-mono text-lime mt-8 mb-4">
              Disclosure of Your Personal Data
            </h2>
            <p className="text-lime font-semibold mb-4">
              We do not sell your local overlay configurations. Ad-related processing may involve verified ad partners to deliver and measure ads.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-3">Security of Your Personal Data</h3>
            <p>
              We apply reasonable measures to protect app functionality and local settings. Ad-related security and processing controls are handled by verified ad partners under their own policies and compliance frameworks.
            </p>

            <h2 className="text-2xl font-bold font-mono text-lime mt-8 mb-4">
              Children&apos;s Privacy
            </h2>
            <p>
              Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us. If We become aware that We have collected Personal Data from anyone under the age of 13 without verification of parental consent, We take steps to remove that information from Our servers.
            </p>
            <p>
              If We need to rely on consent as a legal basis for processing Your information and Your country requires consent from a parent, We may require Your parent&apos;s consent before We collect and use that information.
            </p>

            <h2 className="text-2xl font-bold font-mono text-lime mt-8 mb-4">
              Links to Other Websites
            </h2>
            <p>
              Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party&apos;s site. We strongly advise You to review the Privacy Policy of every site You visit.
            </p>
            <p>
              We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.
            </p>

            <h2 className="text-2xl font-bold font-mono text-lime mt-8 mb-4">
              Changes to this Privacy Policy
            </h2>
            <p>
              We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page.
            </p>
            <p>
              We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the &quot;Last updated&quot; date at the top of this Privacy Policy.
            </p>
            <p>
              You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>

            <h2 className="text-2xl font-bold font-mono text-lime mt-8 mb-4">
              Contact Us
            </h2>
            <p>
              If you have any questions about this Privacy Policy, You can contact us:
            </p>
            <ul className="list-disc ml-6 space-y-2">
              <li>
                By email:{' '}
                <a href="mailto:alimakhloufj.apps@gmail.com" className="text-lime hover:text-lime-hover">
                  alimakhloufj.apps@gmail.com
                </a>
              </li>
              <li>
                By visiting this page on our website:{' '}
                <Link to="/contact" className="text-lime hover:text-lime-hover">
                  http://www.oledburncare.com/contact
                </Link>
              </li>
            </ul>
          </div>
        </article>
      </ScrollReveal>
    </div>
  </main>
);

export default Privacy;